import type { CardStatusKey } from "./labels";

// Planner-style board view logic: filtering and grouping of the cards of one project. Pure, so it can be tested.

export type CardPriorityKey = "URGENT" | "IMPORTANT" | "MEDIUM" | "LOW";
export type CardPreviewKey = "NONE" | "CHECKLIST" | "DESCRIPTION";

export const PRIORITY: Record<CardPriorityKey, { label: string; rank: number }> = {
  URGENT: { label: "Urgent", rank: 0 },
  IMPORTANT: { label: "Important", rank: 1 },
  MEDIUM: { label: "Medium", rank: 2 },
  LOW: { label: "Low", rank: 3 },
};

export type BoardCard = {
  id: number;
  title: string;
  status: CardStatusKey;
  priority: CardPriorityKey;
  preview: CardPreviewKey;
  due: string | null;
  bucketId: number;
  roles: { id: number; name: string }[];
  assignees: { id: number; name: string }[];
  checklistDone: number;
  checklistTotal: number;
  /** First few steps, for the card preview when the checklist is shown on the card. */
  checklistItems: { title: string; done: boolean }[];
  /** First lines of the description, for the card preview when it is shown on the card. */
  excerpt: string | null;
  attachments: number;
  comments: number;
};
export type BoardBucket = { id: number; name: string; color: string | null; cards: BoardCard[] };

/** Header tints a bucket can have (name -> classes). Null is the neutral default. */
export const BUCKET_COLORS: Record<string, { label: string; header: string; column: string; dot: string }> = {
  pink: { label: "Pink", header: "text-pink-700 dark:text-pink-300", column: "bg-pink-50 dark:bg-pink-950/20", dot: "bg-pink-500" },
  red: { label: "Red", header: "text-red-700 dark:text-red-300", column: "bg-red-50 dark:bg-red-950/20", dot: "bg-red-500" },
  yellow: { label: "Yellow", header: "text-yellow-800 dark:text-yellow-300", column: "bg-yellow-50 dark:bg-yellow-950/20", dot: "bg-amber-400" },
  green: { label: "Green", header: "text-green-700 dark:text-green-300", column: "bg-green-50 dark:bg-green-950/20", dot: "bg-green-500" },
  teal: { label: "Teal", header: "text-teal-700 dark:text-teal-300", column: "bg-teal-50 dark:bg-teal-950/20", dot: "bg-teal-500" },
  blue: { label: "Blue", header: "text-blue-700 dark:text-blue-300", column: "bg-blue-50 dark:bg-blue-950/20", dot: "bg-blue-500" },
  purple: { label: "Purple", header: "text-purple-700 dark:text-purple-300", column: "bg-purple-50 dark:bg-purple-950/20", dot: "bg-purple-500" },
  gray: { label: "Gray", header: "text-neutral-700 dark:text-neutral-300", column: "bg-neutral-100 dark:bg-neutral-800/40", dot: "bg-neutral-400" },
};
export const BUCKET_COLOR_NAMES = Object.keys(BUCKET_COLORS);

export const GROUP_BY = [
  { value: "bucket", label: "Bucket" },
  { value: "assigned", label: "Assigned to" },
  { value: "progress", label: "Progress" },
  { value: "due", label: "Due date" },
  { value: "priority", label: "Priority" },
  { value: "labels", label: "Labels" },
] as const;
export type GroupBy = (typeof GROUP_BY)[number]["value"];

export const DUE_KEYS = ["late", "today", "tomorrow", "this-week", "next-week", "future", "none"] as const;
export type DueKey = (typeof DUE_KEYS)[number];
export const DUE_LABEL: Record<DueKey, string> = { late: "Late", today: "Today", tomorrow: "Tomorrow", "this-week": "This week", "next-week": "Next week", future: "Future", none: "No date" };

export const PROGRESS_LABEL: Record<CardStatusKey, string> = { NOT_STARTED: "Not started", IN_PROGRESS: "In progress", WAITING: "Waiting", DONE: "Completed", WAIVED: "Skipped" };

const DAY_MS = 86_400_000;
const t = (day: string) => Date.parse(`${day.slice(0, 10)}T00:00:00.000Z`);
const mondayOf = (ms: number) => {
  const dow = new Date(ms).getUTCDay();
  return ms - (dow === 0 ? 6 : dow - 1) * DAY_MS;
};

/** Where a due date falls relative to `today` (both yyyy-mm-dd). Weeks run Monday to Sunday, as in Planner. */
export function dueKey(due: string | null, today: string): DueKey {
  if (!due) return "none";
  const d = t(due);
  const now = t(today);
  if (d < now) return "late";
  if (d === now) return "today";
  if (d === now + DAY_MS) return "tomorrow";
  const week = mondayOf(now);
  if (d < week + 7 * DAY_MS) return "this-week";
  if (d < week + 14 * DAY_MS) return "next-week";
  return "future";
}

export const isClosed = (s: CardStatusKey) => s === "DONE" || s === "WAIVED";

export type BoardFilters = {
  q: string;
  due: DueKey[];
  /** User ids, or "none" for nobody assigned. */
  assignees: (number | "none")[];
  roles: number[];
  status: CardStatusKey[];
  priority: CardPriorityKey[];
};
export const NO_FILTERS: BoardFilters = { q: "", due: [], assignees: [], roles: [], status: [], priority: [] };

export const filterCount = (f: BoardFilters) => (f.q.trim() ? 1 : 0) + f.due.length + f.assignees.length + f.roles.length + f.status.length + f.priority.length;

/** A card passes when it matches every non-empty facet; inside one facet any chosen value is enough. Closed cards never count as late. */
export function matchesFilters(c: BoardCard, f: BoardFilters, today: string): boolean {
  const q = f.q.trim().toLowerCase();
  if (q && ![c.title, ...c.roles.map((r) => r.name), ...c.assignees.map((a) => a.name)].some((s) => s.toLowerCase().includes(q))) return false;
  if (f.due.length) {
    const k = dueKey(c.due, today);
    if (!f.due.includes(k) || (k === "late" && isClosed(c.status))) return false;
  }
  if (f.assignees.length && !f.assignees.some((a) => (a === "none" ? c.assignees.length === 0 : c.assignees.some((x) => x.id === a)))) return false;
  if (f.roles.length && !c.roles.some((r) => f.roles.includes(r.id))) return false;
  if (f.status.length && !f.status.includes(c.status)) return false;
  if (f.priority.length && !f.priority.includes(c.priority)) return false;
  return true;
}

export type Column = {
  key: string;
  label: string;
  cards: BoardCard[];
  /** Only the bucket grouping has real buckets: cards can be dropped there, and the column has a menu. */
  bucket: BoardBucket | null;
};

/** The columns of the board for a grouping. Buckets keep their order and show empty; other groupings drop empty columns except "No ...". */
export function groupColumns(buckets: BoardBucket[], by: GroupBy, today: string): Column[] {
  const all = buckets.flatMap((b) => b.cards);
  if (by === "bucket") return buckets.map((b) => ({ key: `b${b.id}`, label: b.name, cards: b.cards, bucket: b }));

  const col = (key: string, label: string, cards: BoardCard[]): Column => ({ key, label, cards, bucket: null });
  if (by === "progress") {
    return (Object.keys(PROGRESS_LABEL) as CardStatusKey[]).map((s) => col(s, PROGRESS_LABEL[s], all.filter((c) => c.status === s)));
  }
  if (by === "priority") {
    return (Object.keys(PRIORITY) as CardPriorityKey[]).sort((a, b) => PRIORITY[a].rank - PRIORITY[b].rank).map((p) => col(p, PRIORITY[p].label, all.filter((c) => c.priority === p)));
  }
  if (by === "due") {
    // Completed cards stay in the column of their date; the board folds them under "Completed".
    return DUE_KEYS.map((k) => col(k, DUE_LABEL[k], all.filter((c) => dueKey(c.due, today) === k)));
  }
  if (by === "assigned") {
    const people = new Map<number, string>();
    for (const c of all) for (const a of c.assignees) people.set(a.id, a.name);
    const cols = [...people.entries()].sort((a, b) => a[1].localeCompare(b[1])).map(([id, name]) => col(`u${id}`, name, all.filter((c) => c.assignees.some((a) => a.id === id))));
    return [col("none", "Unassigned", all.filter((c) => c.assignees.length === 0)), ...cols];
  }
  // labels
  const roles = new Map<number, string>();
  for (const c of all) for (const r of c.roles) roles.set(r.id, r.name);
  const cols = [...roles.entries()].sort((a, b) => a[1].localeCompare(b[1])).map(([id, name]) => col(`r${id}`, name, all.filter((c) => c.roles.some((r) => r.id === id))));
  return [col("none", "No label", all.filter((c) => c.roles.length === 0)), ...cols];
}

/** Cards of a column split for display: open ones, then the completed ones that Planner folds away. */
export function splitCompleted(cards: BoardCard[]): { open: BoardCard[]; completed: BoardCard[] } {
  return { open: cards.filter((c) => !isClosed(c.status)), completed: cards.filter((c) => isClosed(c.status)) };
}

/** Closing a card from the check circle: back to where it was is not stored, so reopening means In progress. */
export const toggledStatus = (s: CardStatusKey): CardStatusKey => (isClosed(s) ? "IN_PROGRESS" : "DONE");
